import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Filter, Download, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import api from '../../services/api';

const VendorTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchTransactions();
  }, [filterStatus]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await api.get(`/vendors/my-financial-transactions${params}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const typeMap = {
      'commission': { label: 'Commission', icon: TrendingUp, color: 'text-[#00FF88]' },
      'withdrawal': { label: 'Retrait', icon: TrendingDown, color: 'text-[#FF3B5C]' }
    };
    return typeMap[type] || { label: type, icon: TrendingUp, color: 'text-[#B8B8BA]' };
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'En attente', color: 'bg-[#FFB800]/10 text-[#FFB800]' },
      'available': { label: 'Disponible', color: 'bg-[#00FF88]/10 text-[#00FF88]' },
      'withdrawn': { label: 'Retiré', color: 'bg-[#00FFFF]/10 text-[#00FFFF]' },
      'used': { label: 'Utilisé', color: 'bg-[#B8B8BA]/10 text-[#B8B8BA]' }
    };
    const badge = statusMap[status] || { label: status, color: 'bg-gray-500/10 text-gray-500' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#00FFFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2">📜 Mes Transactions</h1>
          <p className="text-[#B8B8BA]">Historique complet de vos gains et retraits</p>
        </motion.div>

        {/* Filtres */}
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-[#B8B8BA]" />
              <span className="text-[#B8B8BA]">Filtrer par statut:</span>
            </div>
            {['all', 'pending', 'available', 'withdrawn'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filterStatus === status
                    ? 'bg-[#00FFFF] text-[#0A0A0B] font-semibold'
                    : 'bg-[#222226] text-[#B8B8BA] hover:bg-[#3A3A3D]'
                }`}
              >
                {status === 'all' ? 'Tous' : 
                 status === 'pending' ? 'En attente' :
                 status === 'available' ? 'Disponible' :
                 'Retirés'}
              </button>
            ))}
          </div>
        </div>

        {/* Tableau des transactions */}
        <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Historique des transactions</h2>
            <span className="bg-[#00FFFF]/10 text-[#00FFFF] px-4 py-2 rounded-full font-semibold">
              {transactions.length} transaction{transactions.length > 1 ? 's' : ''}
            </span>
          </div>

          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="h-16 w-16 text-[#3A3A3D] mx-auto mb-4" />
              <p className="text-[#B8B8BA]">Aucune transaction pour ce filtre</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#222226]">
                  <tr>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Date</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Type</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Client</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Montant</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Statut</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Référence</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => {
                    const typeInfo = getTypeLabel(tx.type);
                    const TypeIcon = typeInfo.icon;
                    
                    return (
                      <tr key={tx.id} className="border-t border-[#3A3A3D] hover:bg-[#222226] transition-colors">
                        <td className="px-6 py-4 text-[#B8B8BA] text-sm">
                          {formatDate(tx.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <TypeIcon className={`h-4 w-4 ${typeInfo.color}`} />
                            <span className="text-white">
                              {tx.source_type === 'manual_adjustment' && tx.metadata?.reason
                                ? tx.metadata.reason
                                : typeInfo.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-[#B8B8BA]">
                          {tx.metadata?.client_email || '—'}
                        </td>
                        <td className={`px-6 py-4 font-bold ${tx.amount > 0 ? 'text-[#00FF88]' : 'text-[#FF3B5C]'}`}>
                          {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(2)}€
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(tx.status)}
                        </td>
                        <td className="px-6 py-4 text-[#6B6B6E] text-xs font-mono">
                          {tx.source_id?.substring(0, 12) || tx.id.substring(0, 12)}...
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorTransactions;
